<template>
  <div class="about-page">
    <SpreadMessage :bg-url="require('../assets/two-teachers.png')">
      Refund Polcy and Referral Details
    </SpreadMessage>
    <div class="about-main">
      <p>
        We wanted to draft a robust refund policy that reflects our goal to serve you with quality
        education at affordable price, yet also resistant to abuse. We also wanted to help you
        understand how our referral systems work, so we have made a <a href="#refund-tool">tool</a>.
        It will help you understand how your payments and referral bonuses are applied, and how much
        of your money is eligible for refund.
      </p>
      <h3>Breaking down your payment</h3>
      <p>
        The price per month we have on our
        <router-link to="plans">Plans &amp; Pricing</router-link> is the average price per month for
        the duration of the plan. The actual payment break down as follows, for our quarterly and
        yearly plans.
      </p>
      <mtn-banner v-if="onSale">
        <p>
          We're currently running a sale, and the prices in this section reflect the sale price.
        </p>
        <p>
          Lock in your price during sale! If you sign up now, you'll get the sale price for the life
          of your account.
        </p>
      </mtn-banner>
      <table>
        <tr>
          <th>Months</th>
          <th>Price</th>
        </tr>
        <tr>
          <td>1 to {{ quarterlyMonthsCovered }}</td>
          <td>${{ monthlyPrice }}</td>
        </tr>
        <tr>
          <td>3</td>
          <td>{{ quarterlyLastMonthPriceLabel }}</td>
        </tr>
        <tr>
          <td>Total</td>
          <td>${{ quarterlyTotal }}</td>
        </tr>
      </table>

      <table>
        <tr>
          <th>Months</th>
          <th>Price</th>
        </tr>
        <tr>
          <td>1 to {{ yearlyMonthsCovered }}</td>
          <td>${{ monthlyPrice }} per month</td>
        </tr>
        <tr v-if="yearlyLastMonthPrice">
          <td>{{ yearlyMonthsCovered + 1 }}</td>
          <td>${{ yearlyLastMonthPrice }}</td>
        </tr>
        <tr>
          <td>{{ yearlyMonthsCovered + (yearlyLastMonthPrice ? 2 : 1) }} to 12</td>
          <td>Free</td>
        </tr>
        <tr>
          <td>Total</td>
          <td>${{ yearlyTotal }}</td>
        </tr>
      </table>

      <h3>Account period</h3>
      <p>
        Our account period is the same as the number of the days in the month at the start of the
        account period. For instance, if your account period starts on January 1, since January has
        31 days, your account period ends on the 31st. If you start on January 12, counting that as
        your first day, your 31st day falls on Feburary 11.
      </p>
      <p>
        In the tool below, enter the starting date of an account period and it'll show you the end
        date.
      </p>

      <div id="account-period-calc">
        <table>
          <tr>
            <th rowspan="2">Start date</th>
            <th colspan="3">Plan end date</th>
          </tr>
          <tr>
            <th>Monthly</th>
            <th>Quarterly</th>
            <th>Yearly</th>
          </tr>
          <tr>
            <td>
              <input type="text" v-model="accountStartDate" />
            </td>
            <td>
              {{ accountEndDate.month }}
            </td>
            <td>
              {{ accountEndDate.quarter }}
            </td>
            <td>
              {{ accountEndDate.year }}
            </td>
          </tr>
        </table>
      </div>
      <h3 ref="money-back">Refund Policy</h3>
      <p>
        We'll refund any account period that has not been ended. In other words, you have until the
        last day of your account period to request a refund for that month and beyond.
      </p>
      <h3 ref="referral" id="referral-section">Referrals</h3>
      <p>
        If your friend signs up for our service using your referral link, and uses it for a month,
        <em>you</em> will get a month of free service as well! After your fifth referral, each
        referral will get you <em>two</em> free months of service. Your bonus months are applied
        before your paid account periods, including current account period.
      </p>
      <p>Use the tool below to see how many free months of service you can get!</p>
      <div>
        <ReferralCalculator />
      </div>
      <!-- <h3 id="refund-tool">Refund Calculator</h3>
      <p>
        If all this is confusing, we understand. That's why we made a handy tool to help you see how
        your payments and referral bonuses are applied to each account period, and how much is
        eligble for refund for a given plan and refund request date.
      </p>
      <AccountDetails /> -->
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
// import AccountDetails from './account-details'
import ReferralCalculator from './referral-calculator'
import SpreadMessage from './spread-message'
import prices from '../prices'
import MtnBanner from './mtn-banner'

const DATE_FORMAT = 'MM/DD/YYYY'

export default {
  name: 'DetailsPage',
  data() {
    const onSale = !!prices.sale
    let monthlyPrice
    let quarterlyMonthsCovered
    let quarterlyLastMonthPrice
    let yearlyMonthsCovered
    let yearlyLastMonthPrice
    let quarterlyTotal
    let yearlyTotal
    if (onSale) {
      monthlyPrice = prices.sale.perMonth.monthly
      quarterlyMonthsCovered = Math.floor(
        prices.sale.total.quarterly / prices.sale.perMonth.monthly,
      )
      quarterlyLastMonthPrice =
        prices.sale.total.quarterly - prices.sale.perMonth.monthly * quarterlyMonthsCovered
      yearlyMonthsCovered = Math.floor(prices.sale.total.yearly / prices.sale.perMonth.monthly)
      yearlyLastMonthPrice =
        prices.sale.total.yearly - prices.sale.perMonth.monthly * yearlyMonthsCovered
      quarterlyTotal = prices.sale.total.quarterly
      yearlyTotal = prices.sale.total.yearly
    } else {
      monthlyPrice = prices.regular.perMonth.monthly
      quarterlyMonthsCovered = Math.floor(
        prices.regular.total.quarterly / prices.regular.perMonth.monthly,
      )
      quarterlyLastMonthPrice =
        prices.regular.total.quarterly - prices.regular.perMonth.monthly * quarterlyMonthsCovered
      yearlyMonthsCovered = Math.floor(
        prices.regular.total.yearly / prices.regular.perMonth.monthly,
      )
      yearlyLastMonthPrice =
        prices.regular.total.yearly - prices.regular.perMonth.monthly * yearlyMonthsCovered
      quarterlyTotal = prices.regular.total.quarterly
      yearlyTotal = prices.regular.total.yearly
    }
    return {
      accountStartDate: dayjs().format(DATE_FORMAT),
      onSale,
      monthlyPrice,
      quarterlyMonthsCovered,
      quarterlyLastMonthPrice,
      quarterlyTotal,
      yearlyMonthsCovered,
      yearlyLastMonthPrice,
      yearlyTotal,
    }
  },
  components: {
    // AccountDetails,
    ReferralCalculator,
    SpreadMessage,
    MtnBanner,
  },
  mounted() {
    const headerHeight = document.querySelector('.the-header').getBoundingClientRect().height
    const section = this.$route.hash && this.$route.hash.substring(1)
    if (section && ['money-back', 'referral'].includes(section)) {
      // TODO: Fix the bug; behaves eratically when refreshed, or going from
      // one section to another via URL
      const yLocation = this.$refs[section].getBoundingClientRect().top
      window.scrollTo({
        top: yLocation - headerHeight,
        behavior: 'smooth',
      })
    }
  },
  computed: {
    accountEndDate() {
      const startDate = dayjs(this.accountStartDate)
      const month = startDate.add(1, 'month').subtract(1, 'day').format(DATE_FORMAT)
      const quarter = startDate.add(3, 'months').subtract(1, 'day').format(DATE_FORMAT)
      const year = startDate.add(1, 'year').subtract(1, 'day').format(DATE_FORMAT)
      return {
        month,
        quarter,
        year,
      }
    },
    quarterlyLastMonthPriceLabel() {
      return this.quarterlyLastMonthPrice ? `$${this.quarterlyLastMonthPrice}` : 'Free'
    },
  },
}
</script>

<style lang="scss" scoped>
$off-white: #f6f6f6;

.cover {
  font: 40px/1.35em oswald-medium, oswald, sans-serif;
  text-transform: uppercase;
  width: 100%;
  background: url('../assets/two-teachers.png');
  background-size: cover;
  height: calc(100vh - 100px);
  background-position: center center;
  color: white;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  box-sizing: border-box;
  text-shadow: 1px 1px 20px $color-grayscale-900;
}

.about-main {
  font: normal normal normal 15px/1.875em raleway, sans-serif;
  width: 100%;
  max-width: 750px;
  margin: auto;
  padding: 20px;
  box-sizing: border-box;
}
////////////

h2 {
  margin: 0;
  padding: 20px;
  font: normal normal normal 32px/42px 'Oswald', sans-serif;
  text-align: center;
}

.section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  height: 500px;
  position: relative;
  background: $off-white;
}

h3 {
  position: sticky;
  top: 0;
  margin-top: 50px;
}

.section-main {
  overflow: auto;
  padding: 0 20px 20px;
}

.image-holder {
  #money-back & {
    background: url('../assets/two-teachers.png');
    background-size: cover;
  }

  #referral & {
    background: url('../assets/student-writing-2.png');
    background-size: cover;
  }
}
</style>
